import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, Scale, FileText, Award, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';

const RatgeberGutachtenKostenvoranschlagPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Gutachten oder Kostenvoranschlag? Der Unterschied | Ratgeber</title>
        <meta name="description" content="Gutachten vs. Kostenvoranschlag: Was ist besser nach Unfall? Wann brauche ich was? Kosten, Unterschiede, Vor- und Nachteile im Vergleich." />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/ratgeber/gutachten-oder-kostenvoranschlag" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Gutachten oder Kostenvoranschlag? Der entscheidende Unterschied",
            "author": {"@type": "Organization", "name": "Dein Gutachten Direkt", "url": "https://www.dein-gutachten-direkt.de"},
            "publisher": {
              "@type": "Organization", 
              "name": "Dein Gutachten Direkt",
              "logo": {
                "@type": "ImageObject",
                "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
                "width": 200,
                "height": 60
              }
            },
            "image": {
              "@type": "ImageObject",
              "url": "https://customer-assets.emergentagent.com/job_gutachten-preview/artifacts/gusc9edr_image.png",
              "width": 1200,
              "height": 630,
              "caption": "Gutachten oder Kostenvoranschlag Vergleich",
              "creditText": "Dein Gutachten Direkt",
              "creator": {"@type": "Organization", "name": "Dein Gutachten Direkt"},
              "copyrightNotice": "© 2026 Dein Gutachten Direkt"
            },
            "datePublished": "2026-01-18",
            "dateModified": "2026-02-05",
            "description": "Gutachten vs. Kostenvoranschlag: Was ist besser nach Unfall?"
          })}
        </script>
      </Helmet>
      <Header />
      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-6">
          {/* Author & Date Info */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <span className="text-brand-green font-semibold text-sm">Ratgeber</span>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand-green/10 rounded-full flex items-center justify-center">
                  <Award className="w-4 h-4 text-brand-green" />
                </div>
                <span>Geschrieben von: <strong>Dein Gutachten Direkt Team</strong></span>
              </div>
              <span className="hidden sm:inline">•</span>
              <span>Veröffentlicht: <strong>18. Januar 2026</strong></span>
              <span className="hidden sm:inline">•</span>
              <span>Zuletzt aktualisiert: <strong>5. Februar 2026</strong></span>
            </div>
            <p className="text-xs text-gray-500 mt-2">Von erfahrenen Kfz-Sachverständigen</p>
          </div>
          
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-4">Gutachten oder Kostenvoranschlag?</h1>
            <p className="text-xl text-gray-600">Der entscheidende Unterschied und wann Sie was benötigen</p>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Kurz erklärt</h2>
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <h3 className="font-bold text-gray-900 text-lg">Kostenvoranschlag</h3>
                </div>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Erstellt von Werkstatt</li>
                  <li>• Schätzung der Reparaturkosten</li>
                  <li>• Meist kostenlos</li>
                  <li>• Nicht rechtlich bindend</li>
                  <li>• <strong>Keine Wertminderung</strong></li>
                  <li>• <strong>Kein Nutzungsausfall</strong></li>
                </ul>
                <p className="text-xs text-gray-600 mt-4">Geeignet bei: Bagatellschäden unter 750 €</p>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Scale className="w-8 h-8 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Gutachten</h3>
                </div>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Erstellt von Sachverständigem</li>
                  <li>• Detaillierte Schadensdokumentation</li>
                  <li>• Rechtlich belastbar</li>
                  <li>• Kostenlos (Versicherung zahlt)</li>
                  <li>• <strong>Inklusive Wertminderung</strong></li>
                  <li>• <strong>Inklusive Nutzungsausfall</strong></li>
                </ul>
                <p className="text-xs text-gray-600 mt-4">Geeignet bei: Schäden ab 750 €</p>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Wann brauchen Sie ein Gutachten?</h2>
            <p className="text-gray-700 mb-4"><strong>Immer empfehlenswert bei:</strong></p>
            <ul className="space-y-2 text-gray-700 mb-6">
              <li>✓ Schadensumme über 750 € (Faustformel)</li>
              <li>✓ Neueren Fahrzeugen (unter 5 Jahre alt)</li>
              <li>✓ Unklarer Schuldfrage</li>
              <li>✓ Totalschaden-Verdacht</li>
              <li>✓ Wenn Sie alle Ansprüche ausschöpfen möchten</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Was kostet was?</h2>
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-gray-900 mb-3">Kostenvoranschlag</h4>
              <p className="text-gray-700 text-sm"><strong>Kosten für Sie:</strong> Meist 0 € (manche Werkstätten berechnen 50-100 €)</p>
              <p className="text-gray-700 text-sm"><strong>Nachteil:</strong> Sie verzichten auf Wertminderung + Nutzungsausfall (kann 1.000-3.000 €+ sein!)</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h4 className="font-bold text-gray-900 mb-3">Gutachten</h4>
              <p className="text-gray-700 text-sm"><strong>Kosten für Sie:</strong> 0 € (gegnerische Versicherung zahlt)</p>
              <p className="text-gray-700 text-sm"><strong>Vorteil:</strong> Sie erhalten ALLE Ansprüche: Reparatur + Wertminderung + Nutzungsausfall</p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Rechenbeispiel</h2>
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
              <h4 className="font-bold text-gray-900 mb-4">Unfallschaden: 3.000 € Reparaturkosten</h4>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-gray-900 mb-2">Mit Kostenvoranschlag:</p>
                  <p className="text-gray-700 text-sm">Sie erhalten: 3.000 € (nur Reparatur)</p>
                </div>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-2">Mit Gutachten:</p>
                  <ul className="text-gray-700 text-sm space-y-1">
                    <li>Reparaturkosten: 3.000 €</li>
                    <li>Wertminderung: ca. 600 €</li>
                    <li>Nutzungsausfall (10 Tage): 350 €</li>
                    <li>Gutachterkosten: 600 €</li>
                    <li className="font-bold text-brand-green pt-2">Gesamt: 4.550 €</li>
                  </ul>
                </div>
                <div className="bg-brand-green/10 rounded-xl p-4 mt-4">
                  <p className="font-bold text-gray-900">Ihr Vorteil mit Gutachten: +1.550 €</p>
                  <p className="text-gray-600 text-xs mt-1">Alle Kosten zahlt die gegnerische Versicherung</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Fazit</h2>
            <p className="text-gray-700 leading-relaxed">
              Bei Schäden unter 750 € kann ein Kostenvoranschlag ausreichen. Ab 750 € sollten Sie <strong>immer ein unabhängiges Gutachten</strong> wählen – Sie sichern sich damit alle Ansprüche und verlieren kein Geld. Die Kosten trägt die gegnerische Versicherung.
            </p>

            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 my-12 text-center text-white">
              <h3 className="text-2xl font-bold mb-4">Gutachten jetzt beauftragen</h3>
              <p className="text-gray-300 mb-6">Hamburg & 50 km Umgebung • Termin in 30 Minuten</p>
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-3" />
                  Kostenlos anrufen
                </Button>
              </a>
            </div>

            {/* Internal Links Section */}
            <div className="bg-gray-50 rounded-2xl p-8 mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Weitere hilfreiche Ratgeber</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/ratgeber/wertminderung" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Wertminderung nach Unfall</span>
                    <p className="text-sm text-gray-600">Anspruch & Berechnung erklärt</p>
                  </div>
                </Link>
                <Link to="/ratgeber/nach-unfall-checkliste" className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-brand-green hover:shadow-md transition-all group">
                  <ArrowRight className="w-5 h-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  <div>
                    <span className="font-semibold text-gray-900 group-hover:text-brand-green transition-colors">Nach dem Unfall: Checkliste</span>
                    <p className="text-sm text-gray-600">Schritt-für-Schritt Anleitung</p>
                  </div>
                </Link>
              </div>
              
              <h4 className="font-bold text-gray-900 mt-8 mb-4">Gutachten in Ihrer Region</h4>
              <div className="flex flex-wrap gap-3">
                <Link to="/unfallgutachten-hamburg" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Hamburg</Link>
                <Link to="/unfallgutachten-norderstedt" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Norderstedt</Link>
                <Link to="/unfallgutachten-pinneberg" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Pinneberg</Link>
                <Link to="/unfallgutachten-mainz" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Mainz</Link>
                <Link to="/unfallgutachten-frankfurt" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Frankfurt</Link>
                <Link to="/unfallgutachten-wiesbaden" className="text-sm px-4 py-2 bg-white rounded-lg border border-gray-200 hover:border-brand-green hover:text-brand-green transition-all">Wiesbaden</Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default RatgeberGutachtenKostenvoranschlagPage;